import React from 'react';
import { Typography } from '@mui/material';
import { Form2 } from '../../../toolympus/components/primitives/Forms2';
import { PageTitleMenu } from '../../../toolympus/components/primitives/PageTitleMenu';
import {
  LoadingIndicator,
  OccupyFreeSpace,
  SearchField,
} from '../../../toolympus/components/primitives';
import {
  ReorderItemsDialog,
  useReorderItemsBySortorder,
} from '../../../toolympus/components/primitives/ReorderItems';
import { CopTreeMenu } from './Common';
import {
  CopTreeNode,
  updateCopTreeNode,
  useCopTreeNodes,
} from './useCopTreeNodes';
import { useCopTreeFinder } from './useCopTreeFinder';
import { CopTreeNodeEditDialog } from './CopTreeNodeEditDialog';
import { MoveTreeNodeDialog } from './MoveTreeNodeDialog';
import { CopTreeFinder } from './CopTreeFinder';
import { withActionOnSaveItem } from '../../../toolympus/api/useNewItem';

const CopTreeNodeReordering = (props: { item: CopTreeNode }) => {
  return <Typography>{props.item.title}</Typography>;
};

export const CopTreePage = () => {
  const data = useCopTreeNodes();
  const tree = useCopTreeFinder(data);

  const reorderData = useReorderItemsBySortorder<CopTreeNode>({
    updateItemOrder: (item, order) => {
      if (item.sort_order !== order) {
        return updateCopTreeNode(item._id, { sort_order: order });
      }
      return Promise.resolve(item);
    },
    onSave: () => data.reload(),
  });

  const getAncestors = (path?: string | null) => {
    if (!path) return [];
    return path
      .split('.')
      .map(id => tree.nodesById.get(Number(id)))
      .filter((n): n is CopTreeNode => Boolean(n));
  };

  const handleStartMove = (node: CopTreeNode) => {
    const currentParentId = node.path ? Number(node.path.split('.').pop()) : null;
    const currentParent = currentParentId
      ? tree.nodesById.get(currentParentId) ?? null
      : null;

    data.moveNode.startEditing({ node, targetParent: currentParent });
  };

  const moveNode = withActionOnSaveItem(data.moveNode, (savedItem: any) => {
    if (savedItem) {
      tree.selectPathToNode(savedItem.targetParent);
    }
  });

  return (
    <>
      <Form2
        headerItems={
          <>
            <PageTitleMenu current="tree" items={CopTreeMenu} title="COP Tree" />
            <OccupyFreeSpace />
            {data.isLoading && <LoadingIndicator size="small" />}
            <SearchField
              filter={data.filter.filter}
              setFilter={data.filter.setFilter}
              small
              autoFocus
              noButton
            />
          </>
        }
        background="none-padded"
      >
        <CopTreeFinder
          columns={tree.columns}
          selectedPathIds={tree.selectedPathIds}
          matchedNodeIds={tree.matchedNodeIds}
          filterText={tree.filterText}
          onSelect={tree.selectNode}
          onEdit={node => data.editNode.startEditing(node)}
          onDelete={node => data.removeNode.run(node)}
          onAdd={parent => tree.startCreateChild(parent)}
          onMove={handleStartMove}
          onReorder={items => reorderData.startEditing(items)}
          hasChildren={tree.hasChildren}
        />
      </Form2>

      <CopTreeNodeEditDialog
        {...data.newNode}
        isOpen={data.newNode.isEditing}
        close={data.newNode.cancel}
        ancestors={getAncestors(data.newNode.item?.path)}
        isCreate
      />

      <CopTreeNodeEditDialog
        {...data.editNode}
        isOpen={data.editNode.isEditing}
        close={data.editNode.cancel}
        ancestors={getAncestors(data.editNode.item?.path)}
      />

      <MoveTreeNodeDialog
        {...moveNode}
        isOpen={moveNode.isEditing}
        close={moveNode.cancel}
        allNodes={data.data}
      />

      <ReorderItemsDialog
        data={reorderData}
        title="Reorder Nodes"
        itemKey={o => o._id.toString()}
        ItemComponent={CopTreeNodeReordering}
      />
    </>
  );
};